/**
 * Sanitize untrusted HTML for safe Vue v-html rendering.
 * Keeps rich-text formatting; strips scripts, event handlers, and dangerous URLs.
 * Defense-in-depth: backend should still sanitize; this blocks XSS if payload slips through.
 */

const FORBIDDEN_TAGS = {
  script: true,
  iframe: true,
  object: true,
  embed: true,
  link: true,
  meta: true,
  base: true,
  form: true,
  input: true,
  button: true,
  textarea: true,
  select: true,
  svg: true,
  math: true,
  template: true,
  applet: true,
  frame: true,
  frameset: true
}

function isDangerousUrl(value) {
  const v = String(value || '').trim()
  if (!v) return false
  if (/^(javascript|vbscript):/i.test(v)) return true
  if (/^data:\s*text\/html/i.test(v)) return true
  return false
}

function cleanAttributes(el) {
  const attrs = el.attributes ? Array.prototype.slice.call(el.attributes) : []
  attrs.forEach(attr => {
    const name = String(attr.name || '').toLowerCase()
    const value = attr.value || ''
    if (name.indexOf('on') === 0 || name === 'srcdoc' || name === 'xlink:href') {
      el.removeAttribute(attr.name)
      return
    }
    if ((name === 'href' || name === 'src' || name === 'action' || name === 'formaction') && isDangerousUrl(value)) {
      el.removeAttribute(attr.name)
    }
  })
}

function walk(root) {
  const nodes = Array.prototype.slice.call(root.childNodes || [])
  nodes.forEach(node => {
    if (node.nodeType === 8) {
      // drop comments (can hide IE conditional XSS)
      if (node.parentNode) node.parentNode.removeChild(node)
      return
    }
    if (node.nodeType !== 1) return
    const tag = String(node.tagName || '').toLowerCase()
    if (FORBIDDEN_TAGS[tag]) {
      if (node.parentNode) node.parentNode.removeChild(node)
      return
    }
    cleanAttributes(node)
    walk(node)
  })
}

export function sanitizeHtml(dirty) {
  if (dirty == null || dirty === '') return ''
  const html = String(dirty)
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return html
      .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, '')
      .replace(/\son[a-z]+\s*=\s*(['"]).*?\1/gi, '')
      .replace(/\son[a-z]+\s*=\s*[^\s>]+/gi, '')
  }
  const doc = new window.DOMParser().parseFromString(html, 'text/html')
  walk(doc.body || doc.documentElement)
  return doc.body ? doc.body.innerHTML : ''
}

/** Strip HTML to plain text without assigning unsanitized HTML to the live DOM. */
export function stripHtmlToText(dirty) {
  if (dirty == null || dirty === '') return ''
  const safe = sanitizeHtml(dirty)
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return safe.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
  }
  const doc = new window.DOMParser().parseFromString(safe, 'text/html')
  const text = (doc.body && (doc.body.innerText || doc.body.textContent)) || ''
  return String(text).replace(/\s+/g, ' ').trim()
}

export default sanitizeHtml
